import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '../lib/supabaseClient';
import { InboxItem, InboxChannel } from './types';
import { StaffCoverage, coverageKey, marketCondition } from './staffMarkets';
import { Refetcher, createRefetcher } from './refetcher';

export type OwnerScope = 'mine' | 'unclaimed' | 'all';
export type RegionFilter = 'all' | 'quebec' | 'ontario';

export interface InboxFilters {
  ownerScope: OwnerScope;
  needsReplyOnly: boolean;
  /** Empty set means "every channel". */
  channels: Set<InboxChannel>;
  region: RegionFilter;
  search: string;
  /** Only emails from senders nobody has placed (T-236). */
  unmatchedOnly?: boolean;
}

export const defaultFilters: InboxFilters = {
  ownerScope: 'mine',
  needsReplyOnly: false,
  channels: new Set<InboxChannel>(),
  region: 'all',
  search: '',
};

const PAGE_SIZE = 60;

interface UseInboxItemsResult {
  items: InboxItem[];
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
  /** Ids that arrived while this session was open — drives the row flash. */
  arrivedIds: Set<string>;
}

/**
 * The docked inbox's data source.
 *
 * Goes at `inbox_items` through supabase-js rather than react-admin's
 * dataProvider for two reasons: "Unclaimed" is scoped to my markets with an OR
 * (`market is null` OR `market in mine`) which PostgREST expresses natively and
 * ra-data-postgrest does not; and the panel is mounted on every page, so it
 * subscribes to `communications` for live updates instead of polling.
 */
export function useInboxItems(
  filters: InboxFilters,
  currentUserId?: string | null,
  /** My markets; null while loading (nothing is fetched until it's known). */
  coverage?: StaffCoverage | null,
): UseInboxItemsResult {
  const [items, setItems] = useState<InboxItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [arrivedIds, setArrivedIds] = useState<Set<string>>(new Set());

  // Kept in a ref so the realtime subscription never has to be torn down and
  // rebuilt when a filter changes.
  const filtersRef = useRef(filters);
  filtersRef.current = filters;
  const knownIdsRef = useRef<Set<string> | null>(null);
  const seenAtLeastOnce = useRef(false);

  const coverageRef = useRef(coverage);
  coverageRef.current = coverage;

  const fetchItems = useCallback(async () => {
    const f = filtersRef.current;
    const cov = coverageRef.current;
    if (!cov) return;
    const inMyMarkets = marketCondition(cov);
    // With "Needs reply" on, the collapsed "Hung up in menu" group still
    // shows (T-058) — same single read, one OR. Before 20260926030000 runs the
    // column doesn't exist; that read fails and is retried once without it.
    const build = (withMenuHangups: boolean) => {
      let query = supabase
        .from('inbox_items')
        .select('*')
        .order('last_message_at', { ascending: false })
        .limit(PAGE_SIZE);

      // `assignee_id` is the active case owner, else the active lead owner —
      // null means unclaimed (a lead nobody has touched, an unowned case, a
      // bare client). "Mine" is only what's assigned to me, whatever its
      // market — the same set as the header's "N mine", so the two agree.
      // Nobody's work lives under "Unclaimed", scoped to the markets I cover
      // (staff_markets), matching the header's "M unclaimed". "All" is
      // unscoped.
      // Unmatched has no owner and no market yet, so it is everyone's: the scope
      // doesn't apply, or "Mine" would always read empty.
      const scope = f.unmatchedOnly ? 'all' : f.ownerScope;
      if (f.unmatchedOnly) query = query.eq('is_unmatched', true);
      if (scope === 'mine' && currentUserId) {
        query = query.eq('assignee_id', currentUserId);
      }
      const marketOr = scope === 'unclaimed' ? inMyMarkets : null;
      const replyOr =
        f.needsReplyOnly && withMenuHangups ? 'is_unreplied.eq.true,is_menu_hangup.eq.true' : null;
      if (scope === 'unclaimed') query = query.is('assignee_id', null);
      if (f.needsReplyOnly && !replyOr) query = query.eq('is_unreplied', true);
      // Both conditions in one `or` param, ANDed explicitly, rather than
      // relying on how a repeated `or` key is read.
      if (marketOr && replyOr) query = query.or(`and(or(${marketOr}),or(${replyOr}))`);
      else if (marketOr || replyOr) query = query.or((marketOr || replyOr) as string);
      if (f.region !== 'all') query = query.eq('region', f.region);
      if (f.channels.size > 0) {
        query = query.in('last_message_channel', Array.from(f.channels));
      }
      return query;
    };
    try {
      let { data, error: err } = await build(true);
      if (err && f.needsReplyOnly && /is_menu_hangup/.test(err.message ?? '')) {
        ({ data, error: err } = await build(false));
      }
      if (err) throw err;

      const rows = (data ?? []) as InboxItem[];

      // Anything whose id we hadn't seen before is new *to this session*, and
      // gets the arrival flash. The very first load is not an arrival.
      if (knownIdsRef.current) {
        const fresh = new Set<string>();
        for (const row of rows) {
          if (!knownIdsRef.current.has(row.id)) fresh.add(row.id);
        }
        if (fresh.size > 0) {
          setArrivedIds((prev) => new Set([...Array.from(prev), ...Array.from(fresh)]));
          window.setTimeout(() => {
            setArrivedIds((prev) => {
              const next = new Set(prev);
              fresh.forEach((id) => next.delete(id));
              return next;
            });
          }, 2000);
        }
      }
      knownIdsRef.current = new Set(rows.map((r) => r.id));

      setItems(rows);
      setError(null);
      seenAtLeastOnce.current = true;
    } catch (e: any) {
      setError(e?.message || 'Could not load the inbox');
    } finally {
      setIsLoading(false);
    }
  }, [currentUserId]);

  // Refetch when the filters (or my markets) change.
  const filterSignature = useMemo(
    () =>
      [
        filters.ownerScope,
        filters.needsReplyOnly,
        filters.unmatchedOnly,
        filters.region,
        Array.from(filters.channels).sort().join(','),
        coverageKey(coverage ?? null),
      ].join('|'),
    [filters.ownerScope, filters.needsReplyOnly, filters.unmatchedOnly, filters.region, filters.channels, coverage],
  );

  // ── Live updates ──────────────────────────────────────────────────────────
  //
  // `communications` and `inbox_dismissals` are both in the realtime
  // publication. Every change re-runs the view rather than patching a row in
  // place: is_unreplied is derived from the whole conversation, so a local
  // patch would drift. The view is not cheap and every tab of every staff
  // member listens, so the refetch is paced (at most one per 3s) and skipped
  // while the tab is hidden — see refetcher.ts.
  const refetcherRef = useRef<Refetcher | null>(null);
  useEffect(() => {
    const refetcher = createRefetcher(() => void fetchItems(), 3000);
    refetcherRef.current = refetcher;

    const channel = supabase
      .channel('inbox-dock')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'communications' }, refetcher.schedule)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'inbox_dismissals' }, refetcher.schedule)
      .subscribe();

    // A snooze expires on a timer, not on an event — re-read every 5 minutes so
    // a conversation comes back on its own.
    const poll = window.setInterval(refetcher.request, 5 * 60 * 1000);

    return () => {
      refetcher.dispose();
      refetcherRef.current = null;
      window.clearInterval(poll);
      void supabase.removeChannel(channel);
    };
  }, [fetchItems]);

  // Refetch when the filters (or my markets) change — and on mount, deferred
  // until the tab is visible.
  useEffect(() => {
    if (seenAtLeastOnce.current) setIsLoading(false);
    if (refetcherRef.current) refetcherRef.current.request();
    else void fetchItems();
  }, [fetchItems, filterSignature]);

  // Search is applied client-side over the loaded page: it's a filter on what
  // you can see, not a query, and it has to stay instant while typing.
  const visible = useMemo(() => {
    const term = filters.search.trim().toLowerCase();
    if (!term) return items;
    return items.filter((row) =>
      [
        row.contact_name,
        row.contact_phone,
        row.contact_email,
        row.deceased_name,
        row.last_message_body,
        row.last_message_subject,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(term),
    );
  }, [items, filters.search]);

  return {
    items: visible,
    isLoading,
    error,
    refetch: fetchItems,
    arrivedIds,
  };
}

export interface InboxCounts {
  /** Waiting on a reply and assigned to me — the badge. */
  mine: number;
  /** Waiting on a reply, assigned to nobody, in my markets — the header's muted figure. */
  unclaimed: number;
}

/**
 * The numbers of conversations waiting on a reply: mine, and nobody's in the
 * markets I cover.
 *
 * Exact server-side counts, NOT derived from the loaded list — the list is a
 * filtered page of at most 60 rows, so counting it undercounts and the badges
 * disagree with each other. Called once in the provider and shared through
 * context so every badge is literally the same number.
 *
 * Besides new messages and Done/snooze, the counts move when ownership does:
 * a case or a lead assigned to me (both filtered to my id). A lead taken by
 * somebody else leaves my unclaimed figure until the next message, Done or the
 * 5-minute poll — an unfiltered `leads` subscription recounted on every lead
 * edit by anyone, which the view can't afford (T-060).
 *
 * The counts are the same for every tab of this browser, so they are read once
 * and shared: the tab that reads holds a lock, writes the result to
 * localStorage, and the others pick it up from the `storage` event instead of
 * reading themselves. Hidden tabs never read (see refetcher.ts).
 */
const COUNTS_LOCK = 'cleo-inbox-counts';
const COUNTS_STORE = 'cleo-inbox-counts';

interface SharedCounts {
  key: string;
  startedAt: number;
  counts: InboxCounts;
}

function readSharedCounts(key: string): SharedCounts | null {
  try {
    const raw = window.localStorage.getItem(COUNTS_STORE);
    const shared = raw ? (JSON.parse(raw) as SharedCounts) : null;
    return shared?.key === key ? shared : null;
  } catch {
    return null;
  }
}

function writeSharedCounts(shared: SharedCounts) {
  try {
    window.localStorage.setItem(COUNTS_STORE, JSON.stringify(shared));
  } catch {
    // Private window / storage blocked: each tab just reads for itself.
  }
}

export function useInboxCounts(
  currentUserId: string | null,
  coverage: StaffCoverage | null,
): InboxCounts {
  const [counts, setCounts] = useState<InboxCounts>({ mine: 0, unclaimed: 0 });
  const covKey = coverageKey(coverage);
  const inMyMarkets = coverage ? marketCondition(coverage) : null;

  useEffect(() => {
    if (!currentUserId || !coverage) return;
    let cancelled = false;
    const shareKey = `${currentUserId}|${covKey}`;
    let lastApplied = 0;

    const apply = (shared: SharedCounts) => {
      if (cancelled || shared.startedAt < lastApplied) return;
      lastApplied = shared.startedAt;
      setCounts(shared.counts);
    };

    const query = async () => {
      const waiting = () =>
        supabase
          .from('inbox_items')
          .select('id', { count: 'exact', head: true })
          .eq('is_unreplied', true);
      // Unclaimed = nobody's, in the markets I cover.
      const unclaimedQuery = waiting().is('assignee_id', null);
      const startedAt = Date.now();
      const [mine, unclaimed] = await Promise.all([
        waiting().eq('assignee_id', currentUserId),
        inMyMarkets ? unclaimedQuery.or(inMyMarkets) : unclaimedQuery,
      ]);
      if (mine.error || unclaimed.error) return;
      const shared = {
        key: shareKey,
        startedAt,
        counts: { mine: mine.count ?? 0, unclaimed: unclaimed.count ?? 0 },
      };
      writeSharedCounts(shared);
      apply(shared);
    };

    // One tab at a time. A tab that queued behind another's read reuses it
    // when that read started after the change this tab was told about.
    const read = (changedAt: number) => {
      const locks = (navigator as any).locks as LockManager | undefined;
      if (!locks) return void query();
      void locks
        .request(COUNTS_LOCK, async () => {
          const shared = readSharedCounts(shareKey);
          if (shared && shared.startedAt >= changedAt) apply(shared);
          else await query();
        })
        .catch(() => undefined);
    };

    const refetcher = createRefetcher(read, 8000);

    // Another tab read: take its numbers, and skip our own read if theirs
    // already covers what we were waiting on.
    const onStorage = (e: StorageEvent) => {
      if (e.key !== COUNTS_STORE) return;
      const shared = readSharedCounts(shareKey);
      if (!shared) return;
      apply(shared);
      refetcher.satisfiedAt(shared.startedAt);
    };
    window.addEventListener('storage', onStorage);

    // Start from whatever another tab last read, then read fresh (when visible).
    const cached = readSharedCounts(shareKey);
    if (cached) apply(cached);
    refetcher.request();

    const channel = supabase
      .channel('inbox-badge')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'communications' }, refetcher.schedule)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'inbox_dismissals' }, refetcher.schedule)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'funerals',
          filter: `owner_id=eq.${currentUserId}`,
        },
        refetcher.schedule,
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'leads',
          filter: `owner_id=eq.${currentUserId}`,
        },
        refetcher.schedule,
      )
      .subscribe();

    // The safety net for anything realtime missed (a dropped socket, a case
    // taken away from me). It reconciles the badge only — it never alerts.
    const poll = window.setInterval(refetcher.request, 5 * 60 * 1000);

    return () => {
      cancelled = true;
      refetcher.dispose();
      window.removeEventListener('storage', onStorage);
      window.clearInterval(poll);
      void supabase.removeChannel(channel);
    };
    // covKey stands for `coverage` (a new object on every read).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUserId, covKey]);

  return counts;
}

/**
 * The handful of faces shown on the collapsed rail.
 *
 * The six conversations of mine waiting on a reply, newest message first.
 * Scoped like the badge, so the faces and the number agree; unclaimed work is
 * surfaced by the header's figure instead.
 *
 * The rail can still show a conversation the list is currently filtered away
 * from, so clicking one hands the whole row to the thread rather than asking
 * the list to find it.
 */
export function useRailPreview(currentUserId: string | null, limit = 6): InboxItem[] {
  const [rows, setRows] = useState<InboxItem[]>([]);

  useEffect(() => {
    if (!currentUserId) return;
    let cancelled = false;

    const read = async () => {
      // Whole rows, not just the columns the rail draws. It is six of them, so
      // the saving was negligible, and a partial row cannot be handed straight
      // to the thread when one is clicked — which is the point of the rail.
      const { data } = await supabase
        .from('inbox_items')
        .select('*')
        .eq('is_unreplied', true)
        .eq('assignee_id', currentUserId)
        .order('last_message_at', { ascending: false })
        .limit(limit);
      if (!cancelled) setRows((data ?? []) as InboxItem[]);
    };

    // Paced like the badge, and nothing while the tab is hidden.
    const refetcher = createRefetcher(() => void read(), 8000);
    refetcher.request();

    const channel = supabase
      .channel('inbox-rail')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'communications' }, refetcher.schedule)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'inbox_dismissals' }, refetcher.schedule)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'funerals',
          filter: `owner_id=eq.${currentUserId}`,
        },
        refetcher.schedule,
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'leads',
          filter: `owner_id=eq.${currentUserId}`,
        },
        refetcher.schedule,
      )
      .subscribe();

    const poll = window.setInterval(refetcher.request, 5 * 60 * 1000);

    return () => {
      cancelled = true;
      refetcher.dispose();
      window.clearInterval(poll);
      void supabase.removeChannel(channel);
    };
  }, [currentUserId, limit]);

  return rows;
}
